import { FC, useId } from 'react'
import { SeriesPoint } from 'src/services/aiListeningApi/brandProfile.types'
import styles from './charts.module.scss'
import { formatShortDate } from '../utils'

interface AreaChartProps {
  className?: string
  color?: string
  height: number
  points: SeriesPoint[]
  // Date labels under the chart: first, middle and last day.
  showDates?: boolean
}

const WIDTH = 600
// Keeps the line's stroke inside the box at the extremes.
const PAD = 3

// A trend line with a soft fill beneath it. Plain SVG stretched to its box,
// so it follows the card's width without a resize listener.
export const AreaChart: FC<AreaChartProps> = ({
  className,
  color = '#8080ff',
  height,
  points,
  showDates = false,
}) => {
  const gradientId = useId()

  if (points.length < 2) {
    return <div className={className} style={{ height }} />
  }

  const values = points.map((point) => point.value)
  const min = Math.min(...values)
  const max = Math.max(...values)
  const span = max - min

  const x = (index: number) => (index / (points.length - 1)) * WIDTH
  // A flat series has no span to scale by; it is drawn across the middle.
  const y = (value: number) =>
    span === 0
      ? height / 2
      : height - PAD - ((value - min) / span) * (height - PAD * 2)

  const line = values
    .map((value, index) => `${index === 0 ? 'M' : 'L'}${x(index)} ${y(value)}`)
    .join(' ')
  const area = `${line} L${WIDTH} ${height} L0 ${height} Z`
  const middle = points[Math.floor((points.length - 1) / 2)]

  return (
    <div className={className}>
      <svg
        aria-hidden="true"
        className={styles.areaSvg}
        height={height}
        preserveAspectRatio="none"
        viewBox={`0 0 ${WIDTH} ${height}`}
      >
        <defs>
          <linearGradient id={gradientId} x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.28" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={area} fill={`url(#${gradientId})`} />
        <path
          d={line}
          fill="none"
          stroke={color}
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          vectorEffect="non-scaling-stroke"
        />
      </svg>
      {showDates && (
        <div className={styles.areaDates}>
          <span>{formatShortDate(points[0].date)}</span>
          <span>{formatShortDate(middle.date)}</span>
          <span>{formatShortDate(points[points.length - 1].date)}</span>
        </div>
      )}
    </div>
  )
}
